import * as React from "react";
import { cn } from "@/lib/utils";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipContainer = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-[100000] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface TooltipProps extends React.PropsWithChildren {
  content: React.ReactNode;
  disabled?: boolean;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

export function Tooltip({
  children,
  content,
  disabled,
  onOpenChange,
  className,
}: TooltipProps) {
  return (
    <TooltipContainer onOpenChange={onOpenChange}>
      <TooltipTrigger asChild>
        {/* asChild renders nothing for a bare string or number */}
        {React.isValidElement(children) ? children : <span>{children}</span>}
      </TooltipTrigger>
      {!disabled && (
        <TooltipPrimitive.Portal>
          <TooltipContent className={className}>
            {typeof content === "string" && <p>{content}</p>}
            {typeof content !== "string" && content}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      )}
    </TooltipContainer>
  );
}

export { TooltipContainer, TooltipContent, TooltipProvider, TooltipTrigger };
